// CANVAS-BLOCKS: the one Live page (`/c/{slug}/live`, the rail's item), in three views:
//   Workspace (default)  the agentic canvas (CANVAS-CORE), fed by the Live session's channel
//   Browser              the agent screens limited to browser sessions (+ Accounts)
//   Computer             the agent screens limited to desktop sessions (+ Accounts)
// Deep links (`live-link.ts`) pick the view and session; a session not in my overview says "This
// session has ended". An agent's `focus_view` switches the view itself, but never mid-click: a
// person who touched the page in the last few seconds is asked instead. A switch by hand is
// reported to the agent (`interact` switch_view).
import { Button, EmptyState, InfoTip, LocationHeader, ViewTabs } from "@companyos/ui";
import { useQuery } from "@tanstack/react-query";
import { ActivityIcon, LayoutDashboardIcon } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import type { Api } from "../../api/client";
import { findLease, screensOverviewQuery } from "../agent-screens/screens-queries";
import { ScreensScreen } from "../agent-screens/screens-screen";
import { CanvasScreen } from "../canvas/canvas-screen";
import { useCanvasInteract } from "../canvas/interact";
import { type LiveFocus, useLiveCanvas } from "../canvas/live-channel";
import type { CompanyAccess } from "../company";
import { Loading } from "../screen-parts";
import type { LiveView } from "./live-link";

/** How recent a touch keeps the page from switching by itself. */
export const BUSY_MS = 5_000;

export const VIEW_LABEL: Record<LiveView, string> = {
  workspace: "Workspace",
  browser: "Browser",
  computer: "Computer",
};
const VIEW_TABS = (["workspace", "browser", "computer"] as const).map((value) => ({
  value,
  label: VIEW_LABEL[value],
}));

export type LivePageSearch = {
  view?: LiveView;
  session?: string;
  lease?: string;
  tab?: string;
  login?: string;
};

export function LivePage<S extends LivePageSearch>({
  api,
  company,
  search,
  onSearch,
}: {
  api: Api;
  company: CompanyAccess;
  search: S;
  /** Rewrites the page's search (replace, not a new history entry). */
  onSearch: (next: (prev: S) => S) => void;
}) {
  const co = company.id;
  const live = useLiveCanvas(api, co);
  const interact = useCanvasInteract(api, co, live.sessionId);
  const session = search.session ?? search.lease ?? null;
  // An older link (`?lease=`, `?login=`, `?tab=accounts`) without a view: the screens, in the
  // view of that session's kind.
  const implied = !search.view && Boolean(session || search.login || search.tab === "accounts");
  const overview = useQuery({ ...screensOverviewQuery(api, co), enabled: implied && !!session });
  const impliedKind =
    implied && session && overview.data ? findLease(overview.data, session)?.kind : undefined;
  const view: LiveView =
    search.view ?? (implied ? (impliedKind === "desktop" ? "computer" : "browser") : "workspace");

  const go = (v: LiveView, s?: string | null, tab?: string | null) =>
    onSearch((prev) => {
      const { view: _v, session: _s, lease: _l, tab: _t, login: _g, ...rest } = prev;
      return {
        ...rest,
        view: v,
        ...(v !== "workspace" && s ? { session: s } : {}),
        ...(v !== "workspace" && tab ? { tab } : {}),
      } as S;
    });

  // The person's last touch on the page (pointer, key, wheel).
  const lastTouch = useRef(0);
  const touch = () => {
    lastTouch.current = Date.now();
  };
  const [ask, setAsk] = useState<LiveFocus | null>(null);
  // A focus already cached when the page opens is old news: only new frames act.
  const seen = useRef<string | null>(live.focus?.id ?? null);
  const now = useRef({ view, session });
  now.current = { view, session };
  // biome-ignore lint/correctness/useExhaustiveDependencies: once per focus frame
  useEffect(() => {
    const f = live.focus;
    if (!f || seen.current === f.id) return;
    seen.current = f.id;
    const here = now.current;
    if (f.view === here.view && (!f.session || f.session === here.session)) {
      setAsk(null);
      return;
    }
    if (Date.now() - lastTouch.current < BUSY_MS) setAsk(f);
    else {
      setAsk(null);
      go(f.view, f.session, f.tab);
    }
  }, [live.focus]);

  const switchByHand = (v: string) => {
    const next = v === "browser" || v === "computer" ? v : "workspace";
    if (next === view) return;
    setAsk(null);
    go(next);
    interact({ action: "switch_view", detail: next });
  };

  const views = (
    <ViewTabs aria-label="Live views" tabs={VIEW_TABS} value={view} onValueChange={switchByHand} />
  );

  return (
    <div
      className="flex min-h-0 min-w-0 flex-1 flex-col"
      data-slot="live-page"
      data-view={view}
      onPointerDownCapture={touch}
      onKeyDownCapture={touch}
      onWheelCapture={touch}
    >
      {ask ? (
        <div
          role="status"
          data-slot="focus-prompt"
          className="flex min-w-0 flex-wrap items-center gap-2 border-b border-border bg-muted px-6 py-2 text-sm max-md:px-4"
        >
          <span className="font-medium">Switch to {VIEW_LABEL[ask.view]}?</span>
          {ask.reason ? (
            <span className="min-w-0 flex-1 truncate text-muted-foreground">{ask.reason}</span>
          ) : (
            <span className="flex-1" />
          )}
          <Button
            size="sm"
            onClick={() => {
              setAsk(null);
              go(ask.view, ask.session, ask.tab);
            }}
          >
            Switch
          </Button>
          <Button size="sm" variant="ghost" onClick={() => setAsk(null)}>
            Stay
          </Button>
        </div>
      ) : null}
      {view === "workspace" ? (
        live.state.data ? (
          <CanvasScreen
            api={api}
            company={company}
            live={live}
            state={live.state.data}
            views={views}
          />
        ) : (
          <div className="flex min-h-0 min-w-0 flex-1 flex-col" data-slot="workspace-off">
            <LocationHeader icon={ActivityIcon} title="Live" actions={views} />
            {live.state.isPending && live.state.fetchStatus !== "idle" ? (
              <Loading label="Opening the workspace" rows={3} />
            ) : (
              <div className="px-6 py-4 max-md:px-4">
                <EmptyState
                  icon={LayoutDashboardIcon}
                  title="Workspace isn't on yet"
                  description={
                    <span className="inline-flex items-center gap-1">
                      Your agents show boards, calendars and docs here once it is on for this
                      company.
                      <InfoTip label="About the workspace">
                        Browser and Computer show your agents' screens meanwhile.
                      </InfoTip>
                    </span>
                  }
                />
              </div>
            )}
          </div>
        )
      ) : (
        <ScreensScreen
          api={api}
          company={company}
          kind={view === "computer" ? "desktop" : "browser"}
          views={views}
          endedWhenMissing
          selected={session}
          {...(search.tab === "accounts" ? { initialTab: "accounts" as const } : {})}
          {...(search.login ? { login: search.login } : {})}
          onLoginTaken={() =>
            onSearch((prev) => {
              // The view stays put (an older link's view was implied by the login).
              const { login: _done, tab: _tab, ...rest } = prev;
              return { ...rest, view } as S;
            })
          }
          onSelect={(id) =>
            onSearch((prev) => {
              const { lease: _old, session: _s, ...rest } = prev;
              return (id ? { ...rest, view, session: id } : { ...rest, view }) as S;
            })
          }
        />
      )}
    </div>
  );
}
